// Receive delivery (F-FE-N97). The happy path is one click: every line
// prefills to "the rest arrived". Editing a line down asks why, honestly
// ("Received 3 of the 5 still to come"); editing it up is a visible error
// pointing at Quick purchase — never a silent drop. Short deliveries ask the
// one question that matters ("will the rest still arrive?") instead of a
// "Finalize & close" checkbox, and the cash-basis accounting toggle is
// admin-only, collapsed, out of the floor staff's path. After recording, the
// dialog says what the receipt actually did — including the bill it drafted.
import { useEffect, useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { ChevronDown, CheckCircle2 } from 'lucide-react'
import apiClient from '@/api/client'
import type { PurchaseOrderDetail, PoReceiveResult } from '@/types'
import { useCurrency } from '@/contexts/CurrencyContext'
import { Button } from '@/components/ui/button'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { cn, formatDateHuman } from '@/lib/utils'
import { useKitchenFlowEnabled } from '@/hooks/useKitchenFlowEnabled'
import { useItemScanner } from '@/components/store/inventory/useItemScanner'
import { applyScanToList } from '@/lib/scanToLine'
import { PoPhotos } from '@/components/store/purchasing/PoPhotos'
import { IM } from '@/components/store/inventory/itemMasterStrings'
import {
  initReceiveDraft,
  isFullDelivery,
  hasShortLine,
  lineError,
  draftIsValid,
  buildReceivePayload,
  parsedQty,
  type ReceiveDraftLine,
} from './poReceiveDraft'
import { ExpiryDateField } from './inventory/ExpiryFields'
import { businessTodayIso } from '@/lib/expiry'
import { useDayBoundaryHour } from '@/hooks/useDayBoundaryHour'
import { useCogsBasis } from '@/components/admin/expenses/use-cogs-basis'
import { PoReceiveSimpleLine } from './purchasing/PoReceiveSimpleLine'
import { PURCHASING_STRINGS } from './purchasing/purchasingStrings'

type ToastFn = (type: 'success' | 'error', message: string) => void

export function PoReceiveDialog({
  poId,
  onClose,
  isAdmin,
  showToast,
}: {
  poId: string | null
  onClose: () => void
  isAdmin: boolean
  showToast: ToastFn
}) {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { formatCurrency } = useCurrency()
  const open = !!poId

  const { data: detailRes } = useQuery({
    queryKey: ['purchaseOrder', poId],
    queryFn: () => apiClient.getPurchaseOrder(poId!),
    enabled: open,
  })
  const detail: PurchaseOrderDetail | undefined = detailRes?.data

  const [lines, setLines] = useState<ReceiveDraftLine[]>([])
  const today = businessTodayIso(useDayBoundaryHour())
  // Lane A (kitchen flow): capture the supplier's lot number per line.
  const kitchenFlow = useKitchenFlowEnabled()
  // F-RPT-N41: on the purchases basis the server always books the delivery,
  // so the cash-basis tick box would be a choice that does nothing.
  const { data: cogsBasis } = useCogsBasis(isAdmin)
  const alwaysBooked = cogsBasis?.basis === 'purchases'
  const [seededFor, setSeededFor] = useState<string | null>(null)
  const [keepOpen, setKeepOpen] = useState(true)
  const [advancedOpen, setAdvancedOpen] = useState(false)
  const [postToExpense, setPostToExpense] = useState(false)
  const [result, setResult] = useState<PoReceiveResult | null>(null)

  // Kitchen flow lane D: the big-button receiving layout. Same draft, same
  // validation, same payload — only the screen differs. Purchase units come
  // from lane A's columns when a line carries them. Uses lane A's kitchenFlow above.
  const packs = useMemo(() => {
    const m = new Map<string, { packLabel?: string | null; packFactor?: number | null }>()
    for (const ln of detail?.lines ?? []) {
      m.set(ln.id, { packLabel: ln.purchase_unit_label ?? null, packFactor: ln.purchase_unit_factor ?? null })
    }
    return m
  }, [detail])

  // Seed once per opened PO, once the detail is loaded.
  useEffect(() => {
    if (!open) {
      setSeededFor(null)
      setLines([])
      setResult(null)
      setKeepOpen(true)
      setPostToExpense(false)
      setAdvancedOpen(false)
      return
    }
    if (detail && seededFor !== detail.id) {
      setLines(initReceiveDraft(detail, today))
      setSeededFor(detail.id)
    }
  }, [open, detail, seededFor, today])

  // B-P1: one stable idempotency key per open receive session — a retry after
  // a transient error dedups server-side instead of double-posting.
  const receiveKey = useMemo(() => (poId ? crypto.randomUUID() : ''), [poId])

  const full = isFullDelivery(lines)
  const short = hasShortLine(lines)
  // Kitchen flow pilot (Batch 5): each line also needs its price checked.
  const valid = draftIsValid(lines, { priceChecks: kitchenFlow })

  const receivePO = useMutation({
    mutationFn: () => {
      if (!poId) throw new Error('No order selected')
      return apiClient.receivePurchaseOrder(poId, {
        lines: buildReceivePayload(lines),
        finalize: short ? !keepOpen : false,
        idempotency_key: receiveKey || undefined,
        post_to_expense: isAdmin ? postToExpense : false,
      })
    },
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['purchaseOrders'] })
      qc.invalidateQueries({ queryKey: ['purchaseOrder', poId] })
      qc.invalidateQueries({ queryKey: ['stockItems'] })
      qc.invalidateQueries({ queryKey: ['stockSummary'] })
      qc.invalidateQueries({ queryKey: ['stockMovements'] })
      qc.invalidateQueries({ queryKey: ['stockAlerts'] })
      qc.invalidateQueries({ queryKey: ['advancedStockReport'] })
      qc.invalidateQueries({ queryKey: ['inventoryActivity'] })
      qc.invalidateQueries({ queryKey: ['expiringBatches'] }) // F-FE-N118: a lot with an expiry_date was just added
      qc.invalidateQueries({ queryKey: ['stockItem'] }) // open ItemDrawer
      // The receipt drafts a vendor bill — the Vendors tab must not show stale
      // money until a hard refresh (the old dialog forgot both of these).
      qc.invalidateQueries({ queryKey: ['vendorBills'] })
      qc.invalidateQueries({ queryKey: ['vendorLedger'] })
      setResult(res.data ?? {})
    },
    onError: (e: Error) => showToast('error', e.message || 'Failed to record the delivery'),
  })

  const update = (lineId: string, patch: Partial<ReceiveDraftLine>) => {
    setLines((ls) => ls.map((l) => (l.lineId === lineId ? { ...l, ...patch } : l)))
  }

  // Kitchen flow: scan each pack as it comes off the van. The first scan of a
  // line replaces the "everything arrived" pre-fill; each scan adds one pack
  // (a carton code adds its pack size). Over-receipt shows the usual error.
  const [scannedLines, setScannedLines] = useState<Set<string>>(new Set())
  useEffect(() => { setScannedLines(new Set()) }, [poId])
  const scanner = useItemScanner({
    enabled: kitchenFlow && open && !!detail && lines.length > 0,
    onItem: (match) => {
      const out = applyScanToList(lines, (l) => l.stockItemId, parsedQty, scannedLines, (l) => l.lineId, match)
      if (out.kind === 'not_on_list') return { ok: false, text: IM.scan.notOnList(match.item.name, 'order') }
      update(out.line.lineId, { qty: String(out.qty) })
      setScannedLines((s) => new Set(s).add(out.line.lineId))
      return { ok: true, text: IM.scan.added(out.line.itemName, String(out.qty), out.line.unit) }
    },
  })

  const openBill = () => {
    if (!detail) return
    onClose()
    navigate({ to: '/admin/inventory', search: { tab: 'vendors', vendor: detail.supplier_id }, replace: true })
  }

  // ── Post-receive confirmation ─────────────────────────────────────────────
  if (open && result) {
    return (
      <Dialog open onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <CheckCircle2 className="h-4 w-4 text-success-fg" /> Delivery recorded
            </DialogTitle>
            <DialogDescription>
              Stock is updated.
              {result.bill
                ? ` A bill for ${formatCurrency(result.bill.amount)} to ${detail?.supplier_name ?? 'the vendor'} was drafted${result.bill.due_date ? `, due ${formatDateHuman(result.bill.due_date)}` : ''}${isAdmin ? ' — confirm it against their invoice when it comes.' : ' — an admin will confirm it against their invoice when it comes.'}`
                : result.bill_skipped_reason === 'consignment_supplier'
                  ? ' No bill was created — this vendor is on consignment; you settle them from sales.'
                  : result.bill_skipped_reason === 'no_costed_lines'
                    ? ' No bill was created — the received lines carry no cost.'
                    : ''}
              {result.closed_short ? ' The order is closed; the remainder is no longer expected.' : ''}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            {result.bill && (
              <Button variant="outline" onClick={openBill}>Open the bill</Button>
            )}
            <Button onClick={onClose}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl">
            Receive delivery{detail ? ` from ${detail.supplier_name}` : ''}
          </DialogTitle>
          <DialogDescription>
            Everything still to come is filled in — just adjust anything that didn&apos;t arrive as expected.
          </DialogDescription>
        </DialogHeader>
        {!detail ? (
          <p className="text-sm text-muted-foreground py-8">Loading order…</p>
        ) : lines.length === 0 ? (
          <div className="rounded-lg border border-dashed bg-muted/20 p-5 text-sm text-muted-foreground">
            Nothing is left to receive on this order.
          </div>
        ) : (
          <div className="space-y-4 text-sm">
            {kitchenFlow && detail.source_requisition_id && (
              <p className="rounded-md border border-info-border bg-info-surface/50 px-3 py-2 text-sm font-medium">
                {PURCHASING_STRINGS.requisition.banner(detail.source_requisition_no ?? null)}
              </p>
            )}
            {scanner.ui}
            {kitchenFlow && <PoPhotos poId={detail.id} poStatus={detail.status} showToast={showToast} />}
            {kitchenFlow && lines.map((l) => (
              <PoReceiveSimpleLine key={l.lineId} line={{ ...l, ...packs.get(l.lineId) }} onChange={(patch) => update(l.lineId, patch)} />
            ))}
            {!kitchenFlow && lines.map((l) => {
              const err = lineError(l)
              const qty = parsedQty(l)
              const isShort = qty > 0 && qty < l.open - 0.0001
              return (
                <div key={l.lineId} className={cn('border rounded-lg p-4 space-y-3', err === 'over_receipt' && 'border-danger-border')}>
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <span className="font-semibold">
                      {l.itemName} <span className="text-muted-foreground text-sm font-normal">({l.unit})</span>
                    </span>
                    <span className="text-sm text-muted-foreground tabular-nums">{l.open} still to come</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Qty received</Label>
                      <Input
                        type="number"
                        className="tabular-nums"
                        value={l.qty}
                        onChange={(e) => update(l.lineId, { qty: e.target.value })}
                      />
                    </div>
                    <ExpiryDateField
                      id={`po-expiry-${l.lineId}`}
                      item={qty > 0 ? { tracks_expiry: l.tracksExpiry, shelf_life_days: l.shelfLifeDays } : null}
                      value={l.expiry}
                      onChange={(v) => update(l.lineId, { expiry: v })}
                    />
                    <div className="space-y-2">
                      <Label>Unit cost</Label>
                      <Input
                        type="number"
                        step="0.01"
                        className="tabular-nums"
                        value={l.unitCost}
                        onChange={(e) => update(l.lineId, { unitCost: e.target.value })}
                      />
                    </div>
                  </div>
                  {err === 'over_receipt' && (
                    <p className="text-sm font-medium text-danger-fg">
                      That&apos;s more than the {l.open} still to come on this order. To accept extra stock,
                      record the extra as a Quick purchase from the Inventory tab.
                    </p>
                  )}
                  {isShort && (
                    <div className="space-y-2">
                      <Label className={cn('', err === 'needs_reason' && 'text-warning-fg')}>
                        Received {qty} of the {l.open} still to come — why? (required)
                      </Label>
                      <Input
                        placeholder="e.g. short delivery, damaged units rejected…"
                        value={l.reason}
                        onChange={(e) => update(l.lineId, { reason: e.target.value })}
                      />
                    </div>
                  )}
                </div>
              )
            })}

            {short && (
              <div className="rounded-lg border bg-muted/30 p-4 space-y-2">
                <p className="font-medium">Will the rest still arrive?</p>
                <div className="flex flex-col gap-1.5 text-sm">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name="po-keep-open" checked={keepOpen} onChange={() => setKeepOpen(true)} />
                    Yes — keep the order open for another delivery
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name="po-keep-open" checked={!keepOpen} onChange={() => setKeepOpen(false)} />
                    No — close the order, the remainder isn&apos;t coming
                  </label>
                </div>
              </div>
            )}

            {isAdmin && !alwaysBooked && (
              <div className="rounded-lg border">
                <button
                  type="button"
                  className="flex w-full items-center justify-between px-4 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground"
                  aria-expanded={advancedOpen}
                  onClick={() => setAdvancedOpen((v) => !v)}
                >
                  Accounting options
                  <ChevronDown className={cn('h-4 w-4 transition-transform', advancedOpen && 'rotate-180')} aria-hidden />
                </button>
                {advancedOpen && (
                  <label className="flex items-start gap-2 border-t px-4 py-3 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      className="mt-0.5 h-4 w-4"
                      checked={postToExpense}
                      onChange={(e) => setPostToExpense(e.target.checked)}
                    />
                    <span>
                      <span className="font-medium">Book this delivery as an expense today (cash-basis).</span>{' '}
                      Normally stock becomes an expense as it sells (COGS) — leave this off unless your
                      accountant asked for cash-basis.
                    </span>
                  </label>
                )}
              </div>
            )}
          </div>
        )}
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            disabled={!detail || lines.length === 0 || !valid || receivePO.isPending}
            onClick={() => receivePO.mutate()}
          >
            {receivePO.isPending ? 'Recording…' : full ? 'Everything arrived — record it' : 'Record delivery'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
